<template>
  <v-navigation-drawer v-model="model" temporary>
    <v-list>
      <template v-if="authStore.isAuthenticated">
        <template v-for="route in filteredRoutes" :key="route.name">
          <template v-if="route.children?.length">
            <v-list-group :value="route.meta?.title">
              <template v-slot:activator="{ props }">
                <v-list-item v-bind="props" :title="route.meta?.title"></v-list-item>
              </template>
              <template v-for="child in filteredChildren(route.children)" :key="child.name">
                <template v-if="child.children?.length">
                  <v-list-group :value="child.meta?.title">
                    <template v-slot:activator="{ props }">
                      <v-list-item v-bind="props" :title="child.meta?.title"></v-list-item>
                    </template>
                    <template v-for="subChild in filteredChildren(child.children)" :key="subChild.name">
                      <template v-if="subChild.children?.length">
                        <v-list-group :value="subChild.meta?.title">
                          <template v-slot:activator="{ props }">
                            <v-list-item v-bind="props" :title="subChild.meta?.title"></v-list-item>
                          </template>
                          <template v-for="leafChild in filteredChildren(subChild.children)" :key="leafChild.name">
                            <v-list-item
                              :to="`${route.path}/${child.path}/${subChild.path}/${leafChild.path}`"
                              @click="model = false"
                              link
                            >
                              <v-list-item-title>{{ leafChild.meta?.title }}</v-list-item-title>
                            </v-list-item>
                          </template>
                        </v-list-group>
                      </template>
                      <v-list-item
                        v-else
                        :to="`${route.path}/${child.path}/${subChild.path}`"
                        @click="model = false"
                        link
                      >
                        <v-list-item-title>{{ subChild.meta?.title }}</v-list-item-title>
                      </v-list-item>
                    </template>
                  </v-list-group>
                </template>
                <v-list-item v-else :to="`${route.path}/${child.path}`" @click="model = false" link>
                  <v-list-item-title>{{ child.meta?.title }}</v-list-item-title>
                </v-list-item>
              </template>
            </v-list-group>
          </template>
          <v-list-item v-else :to="route.path" @click="model = false" link>
            <v-list-item-title>
              {{ route.meta?.title }}
              <v-badge
                v-if="route.path === '/groups' && invitationsCount > 0"
                :content="invitationsCount"
                color="error"
                inline
                class="ml-2"
              />
            </v-list-item-title>
          </v-list-item>
        </template>

        <v-divider class="my-2"></v-divider>
        <v-list-item @click="handleLogout" link>
          <v-list-item-title class="text-error">Logout</v-list-item-title>
        </v-list-item>
      </template>

      <!-- Show only login when not authenticated -->
      <template v-else>
        <v-list-item to="/login" @click="model = false" link>
          <v-list-item-title>Login</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup>
import { computed, watch } from "vue";
import authStore from "@/store/auth";
import { authManager } from "@/services/authManager";
import { useDynamicRoutes } from "@/router/useDynamicRoutes";
import { useInvitations } from "@/composables/useInvitations";

const model = defineModel();
const { availableRoutes, refreshRoutes } = useDynamicRoutes();
const { count: invitationsCount } = useInvitations();

watch(
  () => authStore.authChanged.value,
  (authState) => {
    if (authState.isAuthenticated) {
      refreshRoutes();
    }
  },
  { immediate: true },
);

const shouldShowRoute = (route) => {
  if (route.meta?.hideInNav) return false;
  if (route.meta?.requiresSuperAdmin && !authStore.isSuperAdmin.value) return false;
  if (route.meta?.requiresAdmin && !authStore.isAdmin.value) return false;
  return true;
};

const filteredChildren = (children) => {
  return children.filter((child) => shouldShowRoute(child));
};

const filteredRoutes = computed(() => {
  const allRoutes = availableRoutes.value;
  const childNames = new Set(allRoutes.flatMap((r) => (r.children || []).map((c) => c.name).filter(Boolean)));

  return allRoutes.filter(
    (route) =>
      route.name &&
      !childNames.has(route.name) &&
      route.meta?.title &&
      !route.meta?.public &&
      route.path !== "/:pathMatch(.*)" &&
      shouldShowRoute(route) &&
      (!route.children?.length || route.children.some((child) => shouldShowRoute(child))),
  );
});

const handleLogout = async () => {
  authManager.userLogout();
  model.value = false;
};
</script>
